import { StarFilledIcon, StarIcon } from '@radix-ui/react-icons';
import { ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Check } from '@/components/icons/barrel';
import { Table } from '@/components/ui';
import { Button } from '@/components/ui/button';
import Empty from '@/components/ui/empty';
import MarketsTableHeader from '@/features/markets/components/markets-table-header';
import {
  MarketsTableTabType,
  MarketsTableVariant,
} from '@/features/markets/types/markets';
import SYMBOL_CATEGORIES, {
  getTrendingSymbols,
} from '@/features/markets/utils/categories';
import WatchListToggle from '@/features/portfolio/components/watch-list-toggle'; // eslint-disable-line import/no-restricted-paths
import useFundedAccount, { isMarketAllowed } from '@/hooks/use-funded-account';
import useFavoritesStore from '@/store/use-favorites-store';
import {
  getAllMarketStoreStates,
  getMarketStore,
} from '@/store/use-markets-store';
import { AssetType, EventType, MarketStatus } from '@/types';
import { sendEvent } from '@/utils/analytics';
import { findBestFuzzyMatch } from '@/utils/fuzzy-search';
import { abbrFromSym } from '@/utils/token-symbol';

import FundingRate, { fundingRateAccessorFn } from './columns/funding-rate';
import IndexPrice, { indexPriceAccessorFn } from './columns/index-price';
import MarkPrice, { markPriceAccessorFn } from './columns/mark-price';
import OpenInterest, { openInterestAccessorFn } from './columns/open-interest';
import PriceChange, { priceChangeAccessorFn } from './columns/price-change';
import Symbol from './columns/symbol';
import QuoteVolume, { quoteVolumeAccessorFn } from './columns/volume';

interface MarketsTableProps {
  id: string;
  variant: MarketsTableVariant;
  containerClassName: string;
  tableClassName?: string;
  rowsPerPage?: number;
  disableDelay?: boolean;
  isInfiniteScroll?: boolean;
  infiniteScrollEstimateRowSize?: number;
  infiniteScrollOverscan?: number;
  onRowClick?: (symbol: string) => void;
}

export default function MarketsTable({
  id,
  variant = 'page',
  containerClassName,
  tableClassName,
  rowsPerPage,
  disableDelay,
  isInfiniteScroll,
  infiniteScrollEstimateRowSize,
  infiniteScrollOverscan,
  onRowClick,
}: MarketsTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [tableTab, setTableTab] = useState<MarketsTableTabType>('*');
  const [search, setSearch] = useState<string>('');
  const [isListingRequested, setIsListingRequested] = useState<boolean>(false);

  const { isFundedOrEval } = useFundedAccount();

  const trendingSymbolsSet = useMemo(() => {
    if (tableTab === 'TRENDING') return getTrendingSymbols();
    return new Set<string>();
  }, [tableTab]);

  const fuzzyMatchSuggestion = useMemo(() => {
    const markets = getAllMarketStoreStates();
    const candidates = Object.entries(markets).map(
      ([symbol, { marketSpec }]) => ({
        symbol,
        abbr: abbrFromSym(symbol, { raw: true }),
        displayAbbr: abbrFromSym(symbol),
        displayName: marketSpec.displayName,
      }),
    );
    return findBestFuzzyMatch(search, candidates);
  }, [search]);

  useEffect(() => {
    if (search.length < 3) return;

    const timeout = window.setTimeout(
      () =>
        sendEvent(EventType.MARKET_SEARCHED, { input: search.toLowerCase() }),
      400,
    );

    return () => {
      window.clearTimeout(timeout);
    };
  }, [search]);

  const requestListing = () => {
    setIsListingRequested(true);
    sendEvent(EventType.MARKET_REQUESTED, {
      input: search,
    });
  };

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setIsListingRequested(false);
  }, []);

  const data = useMemo(() => {
    const markets = getAllMarketStoreStates();
    return Object.keys(markets).filter((symbol) => {
      if (!symbol) return false;
      if (isFundedOrEval && !isMarketAllowed(symbol)) return false;
      const { marketSpec } = markets[symbol];
      if (marketSpec.status !== MarketStatus.TRADING) return false;
      if (search !== '') {
        return (
          abbrFromSym(symbol, { raw: true })
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          abbrFromSym(symbol).toLowerCase().includes(search.toLowerCase()) ||
          marketSpec.displayName.toLowerCase().includes(search.toLowerCase())
        );
      } else {
        switch (tableTab) {
          case '*':
            return true;
          case AssetType.CRYPTO:
          case AssetType.FOREX:
          case AssetType.STOCK: {
            const abbr = abbrFromSym(symbol, { raw: true });
            return (
              marketSpec.asset === tableTab &&
              !SYMBOL_CATEGORIES.Commodities.includes(abbr) &&
              !SYMBOL_CATEGORIES.ETFs.includes(abbr) &&
              !SYMBOL_CATEGORIES.INDICES.includes(abbr)
            );
          }
          case 'TRENDING':
            return trendingSymbolsSet.has(symbol);
          default:
            return SYMBOL_CATEGORIES[tableTab].includes(
              abbrFromSym(symbol, { raw: true }),
            );
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, tableTab, isFundedOrEval]);

  const columns = useMemo<ColumnDef<string>[]>(
    () => [
      {
        id: 'symbol',
        accessorFn: (symbol) => symbol,
        header: t('marketsPage.symbol'),
        cell: ({ row }) => <Symbol symbol={row.original} variant={variant} />,
        sortingFn: (a, b) => {
          if (variant !== 'watchList') {
            const { getIsFavorite } = useFavoritesStore.getState();
            if (getIsFavorite(a.original) && !getIsFavorite(b.original))
              return -1;
            if (!getIsFavorite(a.original) && getIsFavorite(b.original))
              return 1;
          }
          return a.original.localeCompare(b.original);
        },
        meta: {
          width:
            variant === 'watchList'
              ? 'w-[160px] min-w-[160px] max-w-[160px]'
              : 'w-[20%] min-w-80',
        },
      },
      {
        id: 'markPrice',
        accessorFn: markPriceAccessorFn,
        header: t('marketsPage.markPrice'),
        cell: ({ row }) => <MarkPrice symbol={row.original} />,
        meta: {
          width:
            variant === 'watchList'
              ? 'w-[110px] min-w-[110px] max-w-[110px]'
              : 'w-[13%] min-w-44',
        },
      },
      {
        id: 'indexPrice',
        accessorFn: indexPriceAccessorFn,
        header: t('marketsPage.indexPrice'),
        cell: ({ row }) => <IndexPrice symbol={row.original} />,
        meta: {
          width: 'w-[13%] min-w-44',
        },
      },
      {
        id: 'priceChange',
        accessorFn: priceChangeAccessorFn,
        header: t('marketsPage.change24h'),
        cell: ({ row }) => <PriceChange symbol={row.original} />,
        meta: {
          width:
            variant === 'watchList'
              ? 'w-[50%] min-w-[110px]'
              : 'w-[13%] min-w-40',
        },
      },
      {
        id: 'funding',
        accessorFn: fundingRateAccessorFn,
        header: t('marketsPage.funding'),
        cell: ({ row }) => <FundingRate symbol={row.original} />,
        meta: {
          width: 'w-[13%] min-w-40',
        },
      },
      {
        id: 'openInterest',
        accessorFn: openInterestAccessorFn,
        header: t('marketsPage.openInterest'),
        cell: ({ row }) => <OpenInterest symbol={row.original} />,
        meta: {
          width: 'w-[13%] min-w-40',
        },
      },
      {
        id: 'volume',
        accessorFn: quoteVolumeAccessorFn,
        header: t('marketsPage.volume24h'),
        cell: ({ row }) => <QuoteVolume symbol={row.original} />,
        meta: {
          width: 'w-[13%] min-w-40',
        },
      },
      {
        id: 'listed',
        header: ({ table }) => {
          const { rows } = table.getRowModel();
          // eslint-disable-next-line react-hooks/rules-of-hooks
          const hasAll = useFavoritesStore((state) =>
            rows.every((row) => state.favorites.has(row.original)),
          );
          const pageSymbols = rows.map((row) => row.original);
          return (
            <Button
              className="h-8 gap-2"
              size="sm"
              variant={hasAll ? 'contrast' : 'outline'}
              onClick={() => {
                const { addFavorites, removeFavorites } =
                  useFavoritesStore.getState();
                if (hasAll) {
                  removeFavorites(pageSymbols);
                } else {
                  addFavorites(pageSymbols);
                }
              }}
            >
              {hasAll ? (
                <StarFilledIcon width={14} height={14} />
              ) : (
                <StarIcon width={14} height={14} />
              )}
              <span className="hidden sm:block">
                {hasAll
                  ? t('watchListToggle.removeAll')
                  : t('watchListToggle.addAll')}
              </span>
            </Button>
          );
        },
        cell: ({ row }) => (
          <div className="flex justify-end">
            <WatchListToggle symbol={row.original} />
          </div>
        ),
        meta: {
          width:
            'w-[70px] min-w-[70px] max-w-[70px] sm:w-[120px] sm:min-w-[120px] sm:max-w-[120px] sticky right-0 [&>div>div]:ml-auto max-[450px]:!bg-background',
        },
      },
      // {
      //   accessorKey: 'defaultSorting',
      //   header: undefined,
      //   sortingFn: (a, b) => {
      //     if (variant !== 'watchList') {
      //       if (a.original.isFavorite && !b.original.isFavorite) return -1;
      //       if (!a.original.isFavorite && b.original.isFavorite) return 1;
      //     }

      //     if (a.original.mCapIndex === -1 && b.original.mCapIndex > -1)
      //       return 1;
      //     if (a.original.mCapIndex > -1 && b.original.mCapIndex === -1)
      //       return -1;
      //     if (a.original.mCapIndex !== -1 && b.original.mCapIndex !== -1)
      //       return a.original.mCapIndex - b.original.mCapIndex;
      //     return a.original.symbol.localeCompare(b.original.symbol);
      //   },
      // },
    ],
    [t, variant],
  );

  return (
    <>
      <MarketsTableHeader
        tab={tableTab}
        variant={variant}
        onTabChange={setTableTab}
        onSearchChange={handleSearchChange}
      />
      <div className={containerClassName}>
        <div className="absolute inset-0 h-full w-full [&_tr]:cursor-pointer">
          <Table
            id={id}
            columns={columns}
            data={data}
            isMarketPage
            disableDelay={disableDelay}
            isInfiniteScroll={isInfiniteScroll}
            infiniteScrollEstimateRowSize={infiniteScrollEstimateRowSize}
            infiniteScrollOverscan={infiniteScrollOverscan}
            rowsPerPage={rowsPerPage}
            rowModelPagesOffset={1}
            initialSorting={[{ id: 'volume', desc: true }]}
            columnVisibility={
              variant === 'dropdown'
                ? {
                    indexPrice: false,
                    listed: false,
                  }
                : variant === 'watchList'
                  ? {
                      indexPrice: false,
                      funding: false,
                      openInterest: false,
                      volume: false,
                    }
                  : {
                      listed: false,
                    }
            }
            rightGradientClassName={
              variant === 'watchList'
                ? 'right-[70px] sm:right-[120px]'
                : undefined
            }
            emptyComponent={
              <Empty
                title={t('marketsPage.notListedTitle', {
                  search: search.toUpperCase(),
                })}
                description={
                  <div className="flex flex-col items-center justify-center gap-2">
                    {fuzzyMatchSuggestion && data.length === 0 && (
                      <button
                        onClick={() => setSearch(fuzzyMatchSuggestion)}
                        className="mb-3 text-center text-base text-vestgrey-100 transition-colors hover:text-foreground"
                      >
                        Did you mean{' '}
                        <span className="text-blue-400 hover:text-blue-300 font-semibold">
                          {fuzzyMatchSuggestion}
                        </span>
                        ?
                      </button>
                    )}
                    <Button
                      type="button"
                      className="mt-3 gap-2 tracking-wide"
                      disabled={isListingRequested}
                      onClick={requestListing}
                    >
                      {isListingRequested && <Check size={16} />}
                      {isListingRequested
                        ? t('marketsPage.listingRequested', {
                            market: search.toUpperCase(),
                          })
                        : t('marketsPage.requestListing', {
                            market: search.toUpperCase(),
                          })}
                    </Button>
                  </div>
                }
              />
            }
            className={tableClassName}
            onRowClick={(symbol) => {
              navigate(`/trade/${symbol}`);
              onRowClick?.(symbol);
            }}
            getRowClassName={(symbol) => {
              const { marketSpec } = getMarketStore(symbol).getState();
              if (marketSpec.status === MarketStatus.HALT) {
                return 'opacity-75';
              }
              return 'opacity-100';
            }}
          />
        </div>
      </div>
    </>
  );
}
